import CloseIcon from '@mui/icons-material/Close';
import { alpha, AppBar, Box, Dialog, DialogContent, DialogTitle, IconButton, Slide, Toolbar, Typography, useTheme } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import useAxios from '../../hooks/useAxios';
import { OnlyFansSubscriber } from '../../hooks/useSubscriber';
import { VaultMediaResponseList } from '../../hooks/useSubscriberVaultMedias';
import { SettingsContext } from '../../store/SettingsContext';
import { encodeBase64 } from '../../utils/common';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

type Props = {
    subscriber: OnlyFansSubscriber;
    open: boolean;
    onClose: () => void;
    type: 'photo' | 'video' | 'gif' | 'audio';
    item: VaultMediaResponseList;
    title?: string;
};

const MediaLightbox: React.FC<Props> = ({ subscriber, open, onClose, type, item, title }) => {
    const settingsContext = useContext(SettingsContext);

    const theme = useTheme();
    const axios = useAxios();
    const { enqueueSnackbar } = useSnackbar();

    const [signedUrl, setSignedUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!open || !subscriber._id || !item.full) return;

        const fetchSignedUrl = async () => {
            try {
                // if (!item.full?.url) {
                //     setSignedUrl(null);

                //     return;
                // }
                const response = await axios.get(
                    `${settingsContext.routes.onlyFans.base}${subscriber._id}/vaultMediaSignedUrl/${item.id}?url=${encodeURIComponent(
                        encodeBase64(item.full?.url ?? ''),
                    )}`,
                );

                setSignedUrl(response.data.url); // Your server should return: { url: "https://s3.amazonaws.com/..." }
            } catch (err) {
                console.error('Failed to fetch signed URL:', err);

                setSignedUrl(null);
                enqueueSnackbar('Failed to load media. Please try again later.', {
                    variant: 'error',
                });

                onClose(); // Close the dialog if there's an error
            }
        };

        fetchSignedUrl();
    }, [open, item]);

    if (type === 'audio' && signedUrl) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography fontSize={theme.typography.h6.fontSize} sx={{ flexGrow: 1 }}>
                        {title || 'Audio Player'}
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" justifyContent="center" alignItems="center">
                        <audio src={signedUrl} controls autoPlay style={{ width: '100%' }} />
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    // Video and image fallback to full-screen dialog
    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            PaperProps={{
                style: { backgroundColor: alpha(theme.palette.primary.main, 0.9) },
            }}
        >
            <AppBar
                sx={{
                    position: 'relative',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                }}
            >
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                        <CloseIcon sx={{ fontSize: 32 }} />
                    </IconButton>
                    {title && (
                        <Typography variant="h6" sx={{ ml: 2, flex: 1, color: '#fff' }}>
                            {title}
                        </Typography>
                    )}
                </Toolbar>
            </AppBar>

            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                }}
            >
                {type === 'photo' && signedUrl ? (
                    <Box
                        component="img"
                        src={signedUrl}
                        alt="preview"
                        sx={{
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            objectFit: 'contain',
                            borderRadius: 2,
                            boxShadow: 5,
                        }}
                    />
                ) : (
                    <>
                        {signedUrl && (
                            <video
                                src={signedUrl}
                                controls
                                autoPlay
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '90vh',
                                    borderRadius: 8,
                                    boxShadow: theme.shadows[10],
                                }}
                            />
                        )}
                    </>
                )}
            </Box>
        </Dialog>
    );
};

export default MediaLightbox;
